import { IRichBlockTable, IRichBlockTableColumn, IRichBlockTableRow } from '@pure1/data';

/**
 * CSV export for a table block (FR-027, CLOUD-264013).
 *
 * Kept beside the component rather than inside it so the assembly is a pure function with its own
 * spec: the download itself is untestable in a unit test, the text is the part worth pinning.
 *
 * The cell text is the component's, passed in. Two reasons it is not re-derived here. The table
 * formats by column `kind` -- bytes through `FormatSizePipe`, a date-only `datetime` in UTC so it
 * does not render as the day before -- and a second implementation of that would drift from the
 * one on screen. And it is what the rest of the repo does: the Evergreen exporter mirrors its
 * table's pipes rather than the raw model, for the same reason.
 */

/** Renders one cell the way the table shows it, so the export carries what the reader saw. */
export type RichBlockCellFormatter = (row: IRichBlockTableRow, column: IRichBlockTableColumn) => string;

/**
 * RFC 4180-style escape: wrap in double quotes when the field contains a comma, quote, or
 * newline; double up internal quotes. Same rule as the Evergreen exporter.
 */
function escapeCell(value: string): string {
    if (/[",\n\r]/.test(value)) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}

/**
 * The header cell for a column: its label, with the unit appended when it carries one.
 *
 * The unit belongs in the header because it is gone from the cells -- a `percent` column renders
 * "45%" but a `bytes` column renders "1.5 T", and neither is a unit a spreadsheet can infer.
 */
function headerCell(column: IRichBlockTableColumn): string {
    return column.unit ? `${column.label} (${column.unit})` : column.label;
}

/**
 * The block as CSV text: one header row, then one row per row the block carries.
 *
 * The rows are the block's own, in the order the server declared them -- NOT the sorted view. A
 * sort reorders a ten-row sample on screen and is deliberately local to the component
 * (`displayRows` copies before sorting precisely so that copy and export keep reading the declared
 * order). Export follows `copy()` so the two agree; a reader who sorts and then exports gets the
 * same file either way, and the sort is one click to repeat in a spreadsheet.
 */
export function tableBlockToCsv(block: IRichBlockTable, formatCell: RichBlockCellFormatter): string {
    const header = block.columns.map(column => escapeCell(headerCell(column))).join(',');
    const rows = block.rows.map(row => block.columns.map(column => escapeCell(formatCell(row, column))).join(','));
    return [header, ...rows].join('\n');
}

/**
 * The file name, from the block's title when it has one.
 *
 * A title is free text from a model, so it cannot be trusted into a file name: anything that is
 * not alphanumeric becomes a hyphen, runs collapse, and the result is capped. An empty result
 * falls back rather than producing a file called ".csv".
 */
export function tableBlockCsvFilename(block: IRichBlockTable): string {
    const slug = (block.title ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60)
        .replace(/-+$/, '');
    return `${slug || 'copilot-table'}.csv`;
}

/**
 * Build the CSV and hand it to the browser as a download.
 *
 * The BOM is deliberate. Without it Excel reads the file as the system code page, so an array
 * named with anything outside ASCII arrives mojibake -- and these tables are full of customer
 * array names. Every other reader treats a BOM as whitespace.
 */
export function downloadTableBlockCsv(block: IRichBlockTable, formatCell: RichBlockCellFormatter): void {
    const csv = tableBlockToCsv(block, formatCell);
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = tableBlockCsvFilename(block);
    // Firefox refuses to trigger a download when the anchor isn't in the DOM; Chrome and Safari
    // are lenient. Append + remove inside a try/finally so a mid-flight throw still leaves the
    // document clean. Same shape as the Evergreen exporter.
    document.body.appendChild(anchor);
    try {
        anchor.click();
    } finally {
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    }
}
